import * as Tone from 'tone';

class AudioManager {
  constructor() {
    this.players = {};
    this.currentPlayer = null;
    this.currentPose = null;
    this.nextPose = null;
    this.crossfadeDuration = 3.0; // 3 seconds crossfade
    this.isInitialized = false;
    this.totalPoses = 5;
  }

  async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Start Tone.js context - this requires user interaction
      await Tone.start();
      console.log('Audio context started successfully');
      
      // Test that the context is actually running
      if (Tone.context.state === 'running') {
        console.log('AudioContext is running and ready');
        this.isInitialized = true;
      } else {
        throw new Error('AudioContext failed to start properly');
      }
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
      throw error;
    }
  }

  async playPose(poseNumber) {
    if (!this.isInitialized) {
      throw new Error('AudioManager not initialized');
    }

    // Ensure AudioContext is still running
    if (Tone.context.state !== 'running') {
      console.warn('AudioContext is not running, attempting to resume...');
      await Tone.start();
    }

    // Load the audio file if not already loaded
    if (!this.players[poseNumber]) {
      console.log(`Loading audio for pose ${poseNumber}...`);
      await this.loadAudioFile(poseNumber);
    }

    const newPlayer = this.players[poseNumber];

    // Ensure the new player's buffer is fully loaded before crossfading
    if (!newPlayer.buffer || !newPlayer.buffer.loaded) {
      console.log(`Waiting for audio ${poseNumber} buffer to be fully loaded...`);
      await this.waitForBufferLoad(newPlayer, poseNumber);
    } else {
      console.log(`Audio ${poseNumber} already loaded and ready`);
    }

    // If there's a currently playing pose, crossfade to the new one
    if (this.currentPlayer && this.currentPlayer !== newPlayer) {
      console.log(`Crossfading from pose ${this.currentPose} to pose ${poseNumber}`);
      await this.crossfade(this.currentPlayer, newPlayer);
    } else if (!this.currentPlayer) {
      // First time playing - fade in smoothly
      console.log(`Starting first audio for pose ${poseNumber} with fade in`);
      newPlayer.volume.value = -60; // Start silent
      newPlayer.start();
      
      // Smooth fade in over 1 second
      const fadeInSteps = 20;
      const fadeInDuration = 1.0; // 1 second fade in
      const stepDuration = fadeInDuration / fadeInSteps;
      
      for (let i = 0; i <= fadeInSteps; i++) {
        const progress = i / fadeInSteps;
        const volume = -60 + (60 * progress); // -60dB to 0dB
        newPlayer.volume.value = volume;
        
        // Wait for this step
        await new Promise(resolve => setTimeout(resolve, stepDuration * 1000));
      }
      
      console.log(`First audio fade in completed for pose ${poseNumber}`);
    }

    // Update current pose and preload next
    this.currentPose = poseNumber;
    this.currentPlayer = newPlayer;
    
    // Preload the next audio in sequence for seamless crossfade
    await this.preloadNext();
  }

  async loadAudioFile(poseNumber) {
    console.log(`Loading audio file ${poseNumber}...`);
    
    try {
      // Create the player
      const player = new Tone.Player({
        url: `/audio/${poseNumber}.ogg`,
        loop: true,
        autostart: false,
        volume: 0 // Start at 0, will be faded in
      }).toDestination();
      
      // Store the player immediately
      this.players[poseNumber] = player;
      
      // Wait for the buffer to load using Tone.js buffer loading
      await new Promise((resolve, reject) => {
        const checkLoaded = () => {
          console.log(`Checking buffer for ${poseNumber}:`, {
            hasBuffer: !!player.buffer,
            bufferState: player.buffer?.state,
            bufferLoaded: player.buffer?.loaded
          });
          
          if (player.buffer && player.buffer.loaded) {
            console.log(`Audio ${poseNumber} loaded successfully`);
            resolve();
          } else if (player.buffer && player.buffer.state === 'error') {
            console.error(`Failed to load audio ${poseNumber}: buffer error`);
            reject(new Error(`Failed to load audio ${poseNumber}`));
          } else {
            // Check again in 100ms
            setTimeout(checkLoaded, 100);
          }
        };
        
        // Start checking after a short delay
        setTimeout(checkLoaded, 100);
        
        // Add timeout
        setTimeout(() => {
          reject(new Error(`Timeout loading audio ${poseNumber}`));
        }, 10000);
      });
      
    } catch (error) {
      console.error(`Error loading audio ${poseNumber}:`, error);
      throw error;
    }
  }

  async waitForBufferLoad(player, poseNumber) {
    return new Promise((resolve, reject) => {
      const checkLoaded = () => {
        if (player.buffer && player.buffer.loaded) {
          console.log(`Audio ${poseNumber} buffer is fully loaded and ready`);
          resolve();
        } else {
          setTimeout(checkLoaded, 50); // Check every 50ms
        }
      };
      checkLoaded();
      
      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error(`Timeout waiting for audio ${poseNumber} buffer to load`));
      }, 10000);
    });
  }

  async preloadNext() {
    if (!this.currentPose) return;
    
    // Calculate next and previous poses in sequence
    const nextPoseNumber = this.currentPose === this.totalPoses ? 1 : this.currentPose + 1;
    const prevPoseNumber = this.currentPose === 1 ? this.totalPoses : this.currentPose - 1;
    
    // Preload both next and previous for zero-lag experience
    const preloadPromises = [];
    
    // Preload next pose
    if (!this.players[nextPoseNumber]) {
      console.log(`Preloading next audio: pose ${nextPoseNumber}`);
      preloadPromises.push(
        this.loadAudioFile(nextPoseNumber).then(() => {
          console.log(`Next audio preloaded: pose ${nextPoseNumber} ready`);
        }).catch(error => {
          console.warn(`Failed to preload next audio ${nextPoseNumber}:`, error);
        })
      );
    } else {
      console.log(`Next audio already loaded: pose ${nextPoseNumber} ready`);
    }
    
    // Preload previous pose
    if (!this.players[prevPoseNumber]) {
      console.log(`Preloading previous audio: pose ${prevPoseNumber}`);
      preloadPromises.push(
        this.loadAudioFile(prevPoseNumber).then(() => {
          console.log(`Previous audio preloaded: pose ${prevPoseNumber} ready`);
        }).catch(error => {
          console.warn(`Failed to preload previous audio ${prevPoseNumber}:`, error);
        })
      );
    } else {
      console.log(`Previous audio already loaded: pose ${prevPoseNumber} ready`);
    }
    
    // Wait for all preloading to complete
    if (preloadPromises.length > 0) {
      await Promise.all(preloadPromises);
      console.log(`All adjacent audio files preloaded for pose ${this.currentPose}`);
    }
  }

  async crossfade(outPlayer, inPlayer) {
    console.log('Starting two-stage crossfade...');
    
    // Ensure both players are ready
    if (!outPlayer.buffer || !outPlayer.buffer.loaded) {
      throw new Error('Outgoing player buffer not ready for crossfade');
    }
    if (!inPlayer.buffer || !inPlayer.buffer.loaded) {
      throw new Error('Incoming player buffer not ready for crossfade');
    }
    
    // Start the new player at volume -60dB (very quiet) and ensure it's playing
    inPlayer.volume.value = -60;
    inPlayer.start();
    
    // Wait a moment to ensure the new player is actually playing
    await new Promise(resolve => setTimeout(resolve, 50));
    
    console.log('New player started, beginning two-stage crossfade...');

    // STAGE 1: Fade in new audio to 100% while old audio stays at 100%
    console.log('Stage 1: Fading in new audio to 100%...');
    const fadeInSteps = 15; // 15 steps for fade in
    const fadeInDuration = this.crossfadeDuration / 2; // 1.5 seconds for fade in
    const stepDuration = fadeInDuration / fadeInSteps;
    
    for (let i = 0; i <= fadeInSteps; i++) {
      const progress = i / fadeInSteps;
      
      // Only fade in new audio, old audio stays at full volume
      const inVolume = -60 + (60 * progress); // -60dB to 0dB
      
      inPlayer.volume.value = inVolume;
      
      // Wait for this step
      await new Promise(resolve => setTimeout(resolve, stepDuration * 1000));
    }
    
    console.log('Stage 1 complete: Both audios at 100% volume');
    
    // STAGE 2: Fade out old audio while new audio stays at 100%
    console.log('Stage 2: Fading out old audio...');
    const fadeOutSteps = 15; // 15 steps for fade out
    const fadeOutDuration = this.crossfadeDuration / 2; // 1.5 seconds for fade out
    const fadeOutStepDuration = fadeOutDuration / fadeOutSteps;
    
    for (let i = 0; i <= fadeOutSteps; i++) {
      const progress = i / fadeOutSteps;
      
      // Only fade out old audio, new audio stays at full volume
      const outVolume = 0 - (60 * progress); // 0dB to -60dB
      
      outPlayer.volume.value = outVolume;
      
      // Wait for this step
      await new Promise(resolve => setTimeout(resolve, fadeOutStepDuration * 1000));
    }
    
    console.log('Two-stage crossfade completed, stopping old player');

    // Stop the old player after crossfade completes
    outPlayer.stop();
  }

  stop() {
    if (this.currentPlayer) {
      console.log('Stopping current audio');
      this.currentPlayer.stop();
      this.currentPlayer = null;
      this.currentPose = null;
    }
  }

  // Clean up resources
  dispose() {
    Object.values(this.players).forEach(player => {
      player.dispose();
    });
    this.players = {};
    this.currentPlayer = null;
    this.isInitialized = false;
  }
}

export default AudioManager;
